import React, { useState, useContext, useRef, useEffect } from "react";
import axios from "axios";
import { OperaContext } from "../../context/OperaContext";
import ImageModal from "../../components/ImagePreviewModal";
import useGoogleDriveImagePicker from "../../components/useGoogleDriveImagePicker";
import ImagePreview from "../../components/imagePreview";
import { toast } from "react-toastify";
import ImageCropModal from "./Event/_components/image-crop-modal";
import RichTextEditor from "../../components/RichTextEditor";

function PerformerTab() {
  const {
    allFamousPerformers = [],
    setAllFamousPerformers = () => { },
    fetchData,
  } = useContext(OperaContext);
  const [newPerformer, setNewPerformer] = useState({
    name: "",
    description: "",
    image: null,
  });
  const [editingPerformer, setEditingPerformer] = useState(null);
  const [hiddenPerformers, setHiddenPerformers] = useState([]);
  const [showHiddenPerformers, setShowHiddenPerformers] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const performerFormRef = useRef(null);
  const formContainerRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [cropField, setCropField] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const openModal = (image) => {
    setModalImage(image);
    setModalOpen(true);
  };

  // Sort and filter performers based on search term
  const sortedVisiblePerformers = [...allFamousPerformers]
    .sort((a, b) => a.name?.localeCompare(b.name))
    .filter((performer) =>
      performer.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const sortedHiddenPerformers = [...hiddenPerformers]
    .sort((a, b) => a.name?.localeCompare(b.name))
    .filter((performer) =>
      performer.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const handlePerformerChange = (e) => {
    const { name, value } = e.target;
    setNewPerformer((prev) => ({ ...prev, [name]: value }));
    if (editingPerformer)
      setEditingPerformer((prev) => ({ ...prev, [name]: value }));
  };

  const handlePerformerFileChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file (e.g., JPG, PNG)");
      e.target.value = null;
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Image file size must be less than 5MB");
      e.target.value = null;
      return;
    }

    setError(null);

    if (name === "coverImage") {
      setOriginalFile(file);
      setCropField(name);

      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setIsCropOpen(true);
      };
      reader.readAsDataURL(file);
    } else {
      setNewPerformer((prev) => ({ ...prev, [name]: file }));
      if (editingPerformer) {
        setEditingPerformer((prev) => ({ ...prev, [name]: file }));
      }
    }
  };

  const handleDescriptionChange = (field, value) => {
    setNewPerformer((prev) => ({ ...prev, [field]: value }));
    if (editingPerformer) {
      setEditingPerformer((prev) => ({ ...prev, [field]: value }));
    }
  };
  const resetForm = () => {
    setShowPreview(false);
    setNewPerformer({
      name: "",
      description: "",
      image: null,
    });
    if (performerFormRef.current) {
      performerFormRef.current.reset();
    }
  };

  const cancelEditing = () => {
    setEditingPerformer(null);
    resetForm();
  };

  const fetchHiddenPerformers = async () => {
    try {
      const res = await axios.get("/api/famous-performers/hidden", {
        timeout: 15000,
      });
      setHiddenPerformers(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (err) {

      setError("Failed to load hidden performers.");
      setHiddenPerformers([]);
    }
  };

  const submitPerformer = async (formData, isEdit = false, id = null) => {
    setShowPreview(false);
    try {
      const url = isEdit
        ? `/api/famous-performers/${id}`
        : "/api/famous-performers";
      const method = isEdit ? "put" : "post";
      const res = await axios({
        method,
        url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.status === 200 || res.status === 201) {
        toast.success(`${isEdit ? "Updated" : "Added"} performer Successfully`);
      }
      setError(null);
      return res.data;

    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        `Failed to ${isEdit ? "update" : "add"} performer`;
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const handlePerformerSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("name", newPerformer.name);
    formData.append("description", newPerformer.description || ""); // Ensure description is never undefined
    if (editingPerformer) {
      formData.append("image", editingPerformer?.image);
      formData.append("coverImage", editingPerformer?.coverImage)
    }
    if (!editingPerformer) {
      formData.append("image", newPerformer?.image);
      formData.append("coverImage", newPerformer?.coverImage);
    }

    try {
      setError(null);
      const result = await submitPerformer(
        formData,
        !!editingPerformer,
        editingPerformer?._id
      );

      // Update local state immediately for better UX
      if (editingPerformer) {
        setAllFamousPerformers((prev) =>
          prev.map((p) => (p._id === editingPerformer._id ? result : p))
        );
      } else {
        setAllFamousPerformers((prev) => [...prev, result]);
      }

      setEditingPerformer(null);
      resetForm();
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const editPerformer = (performer) => {
    setEditingPerformer(performer);
    setNewPerformer({
      name: performer.name,
      description: performer.description || "",
      image: performer.image,
    });
    setSearchTerm("");
  };

  const deletePerformer = async (
    performerId,
    isHidden = false,
    retryCount = 0,
    maxRetries = 3
  ) => {
    if (!window.confirm("Are you sure you want to delete this performer?"))
      return;
    try {
      await axios.delete(`/api/famous-performers/${performerId}`, {
        timeout: 15000,
      });
      if (isHidden) {
        setHiddenPerformers((prev) =>
          prev.filter((p) => p._id !== performerId)
        );
      } else {
        setAllFamousPerformers((prev) =>
          prev.filter((p) => p._id !== performerId)
        );
      }
      setEditingPerformer(null);
      resetForm();
      fetchData();
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete performer");
    }
  };

  const hidePerformer = async (performerId) => {
    try {
      await axios.put(
        `/api/famous-performers/${performerId}/hide`,
        {},
        { timeout: 15000 }
      );
      setAllFamousPerformers((prev) =>
        prev.filter((p) => p._id !== performerId)
      );
      fetchData();
      fetchHiddenPerformers();
      setError(null);
    } catch (err) {
      setError("Failed to hide performer");
    }
  };

  const unhidePerformer = async (
    performerId
  ) => {
    try {
      const res = await axios.put(
        `/api/famous-performers/${performerId}/unhide`,
        {},
        { timeout: 15000 }
      );
      setHiddenPerformers((prev) => prev.filter((p) => p._id !== performerId));
      setAllFamousPerformers((prev) => [...prev, res.data]);
      fetchData();
      setError(null);
    } catch (err) {
      setError("Failed to unhide performer");
    }
  };

  const publishPerformer = async (performerId) => {
    try {
      const res = await axios.put(
        `/api/famous-performers/${performerId}/publish`,
        {},
        { timeout: 15000 }
      );
      if (res.status === 200) {
        toast.success("Successfully Published performer.");
      }
      setAllFamousPerformers((prev) =>
        prev.map((p) => (p._id === performerId ? { ...p, published: true } : p))
      );
      fetchHiddenPerformers();
      setError(null);
    } catch (err) {
      toast.error("Error publishing performer:", err.message);
    }
  };

  const unpublishPerformer = async (performerId) => {
    try {
      const res = await axios.put(
        `/api/famous-performers/${performerId}/unpublish`,
        {},
        { timeout: 15000 }
      );
      if (res.status === 200) {
        toast.success("Successfully unpublished performer!");
      }

      setAllFamousPerformers((prev) =>
        prev.map((p) =>
          p._id === performerId ? { ...p, published: false } : p
        )
      );
      fetchHiddenPerformers();
    } catch (err) {
      toast.error("Error to unpublish performer:", err.message);
    }
  };

  useEffect(() => {
    if (showHiddenPerformers) fetchHiddenPerformers();
    else setHiddenPerformers([]);
  }, [showHiddenPerformers]);

  // Scroll to form when editing a performer
  useEffect(() => {
    if (editingPerformer && formContainerRef.current) {
      setTimeout(() => {
        const yOffset = -70;
        const y =
          formContainerRef.current.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }, 100);
    }
  }, [editingPerformer]);
  const handleOpenPicker = useGoogleDriveImagePicker({
    isEditing: editingPerformer ? true : false,
    setFormData: setNewPerformer,
    setFormDataEdit: setEditingPerformer,
    setError,
  });
  console.log(newPerformer);
  return (
    <div className="max-w-[1280px] mx-auto p-4 md:p-8 bg-[#1a1a1a] rounded-lg text-white flex flex-col gap-4">
      <ImageCropModal
        imageSrc={imageSrc}
        originalFileName={originalFile?.name || cropField || "Performer"}
        isOpen={isCropOpen}
        onClose={() => setIsCropOpen(false)}
        onSave={(croppedFile) => {
          setIsCropOpen(false);

          setNewPerformer((prev) => ({ ...prev, [cropField]: croppedFile }));
          if (editingPerformer) {
            setEditingPerformer((prev) => ({ ...prev, [cropField]: croppedFile }));
          }
        }}
      />
      <ImageModal
        open={modalOpen}
        image={modalImage}
        onClose={() => setModalOpen(false)}
      />
      <div ref={formContainerRef} id="performer-form-container">
        <h2 className="text-xl text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mb-4">
          {editingPerformer ? "Edit Artist" : "Add New Artist"}
        </h2>
        {error && <p className="text-[#f44336] text-center mb-4">{error}</p>}
        <form
          onSubmit={handlePerformerSubmit}
          ref={performerFormRef}
          className="flex flex-col gap-4 mb-8"
          id="performer-form"
        >
          <input
            type="text"
            name="name"
            value={newPerformer.name}
            onChange={handlePerformerChange}
            placeholder="Performer Name"
            className="p-2 bg-[#333] border border-[#555] rounded text-white text-base"
            required
          />
          <button
            type="button"
            className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors disabled:bg-[#e6c347] disabled:cursor-not-allowed"
            onClick={() => setShowPreview((prev) => !prev)}
          >
            {showPreview ? "Edit Text" : "Edit HTML"}
          </button>
          {showPreview && <textarea
            name="description"
            value={newPerformer.description}
            onChange={handlePerformerChange}
            placeholder="Performer Description"
            className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md min-h-[150px] resize-y"
          />}
          {!showPreview && <RichTextEditor
            value={newPerformer.description}
            height={200}
            onChange={(content) =>
              handleDescriptionChange("description", content)
            }
          />}

          {(editingPerformer?.image || newPerformer.image) && (
            <ImagePreview
              image={editingPerformer?.image || newPerformer.image}
              onClick={openModal}
            />
          )}
          <p className="-mb-3">Image</p>
          <div className="flex gap-2">
            <div className="relative">
              <input
                type="file"
                id="performer-image-file"
                name="image"
                onChange={handlePerformerFileChange}
                accept="image/*"
                className="pl-10 pr-3 py-2 bg-[#333] border border-[#555] rounded text-white"
              />
              <label
                htmlFor="performer-image-file"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 cursor-pointer pointer-events-none"
              >
                <img
                  src="/images/attach.svg"
                  alt="Choose File"
                  className="w-4 h-4 filter invert"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={() => handleOpenPicker("image")}
              className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
            >
              <img
                src="/images/drive.png"
                alt="Google Drive"
                className="w-4 h-4"
              />
              Google Drive
            </button>
          </div>
          {(editingPerformer?.coverImage || newPerformer.coverImage) && (
            <ImagePreview
              image={editingPerformer?.coverImage || newPerformer.coverImage}
              onClick={openModal}
            />
          )}
          <p className="-mb-3">Cover Image</p>
          <div className="flex gap-2">
            <div className="relative">
              <input
                type="file"
                id="performer-cover-image-file"
                name="coverImage"
                onChange={handlePerformerFileChange}
                accept="image/*"
                className="pl-10 pr-3 py-2 bg-[#333] border border-[#555] rounded text-white"
              />
              <label
                htmlFor="performer-cover-image-file"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 cursor-pointer pointer-events-none"
              >
                <img
                  src="/images/attach.svg"
                  alt="Choose File"
                  className="w-4 h-4 filter invert"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={() => handleOpenPicker("image")}
              className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
            >
              <img
                src="/images/drive.png"
                alt="Google Drive"
                className="w-4 h-4"
              />
              Google Drive
            </button>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded transition-colors cursor-pointer"
            >
              {editingPerformer ? "Update Artist" : "Add Artist"}
            </button>
            {editingPerformer && (
              <>
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="px-4 py-2 bg-[#666] hover:bg-[#555] text-white rounded transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() =>
                    deletePerformer(
                      editingPerformer._id,
                      editingPerformer.hidden
                    )
                  }
                  className="px-4 py-2 bg-[#f44336] hover:bg-[#da190b] text-white rounded transition-colors cursor-pointer"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </form>
      </div>

      {/* Search bar */}
      <div div className="mb-4" >
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search artist..."
          className="p-2 bg-[#333] border border-[#555] rounded text-white text-base w-full"
        />
      </div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left">
          Visible Artists
        </h3>
        <div className="flex justify-center md:justify-start mb-4">
          <label className="flex items-center gap-2 text-white cursor-pointer">
            <input
              type="checkbox"
              checked={showHiddenPerformers}
              onChange={(e) => setShowHiddenPerformers(e.target.checked)}
              className="h-4 w-4 text-[#d4a017] bg-[#333] border-[#555] rounded"
            />
            Show Hidden Artists
          </label>
        </div>
      </div>

      <div>
        <ul className="list-none p-0">
          {sortedVisiblePerformers.length === 0 ? (
            <p className="text-[#ccc] text-center">
              {searchTerm
                ? "No visible artists match your search."
                : "No visible artists available."}
            </p>
          ) : (
            sortedVisiblePerformers.map((performer) => (
              <li
                key={performer._id}
                className="flex flex-col md:flex-row justify-between items-center py-2 border-b border-[#444]"
              >
                <span className="text-white flex-1 text-center md:text-left">
                  {performer.name}
                </span>
                <div className="flex flex-row gap-2 mt-2 md:mt-0">
                  <button
                    onClick={() => editPerformer(performer)}
                    className="px-3 py-1 bg-[#4CAF50] hover:bg-[#45a049] text-white text-sm rounded transition-colors cursor-pointer"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => hidePerformer(performer._id)}
                    className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded transition-colors cursor-pointer"
                  >
                    Hide
                  </button>
                  {performer.published ? (
                    <button
                      onClick={() => unpublishPerformer(performer._id)}
                      className="px-3 py-1 bg-[#d4a017] hover:bg-[#b38b00] text-white text-sm rounded transition-colors cursor-pointer"
                    >
                      Unpublish
                    </button>
                  ) : (
                    <button
                      onClick={() => publishPerformer(performer._id)}
                      className="px-3 py-1 bg-[#d4a017] hover:bg-[#b38b00] text-white text-sm rounded transition-colors cursor-pointer"
                    >
                      Publish
                    </button>
                  )}
                </div>
              </li>
            ))
          )}
        </ul>
      </div>

      {
        showHiddenPerformers && (
          <>
            <h3 className="text-lg text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mt-4 mb-4">
              Hidden Artists
            </h3>
            <div>
              {sortedHiddenPerformers.length === 0 ? (
                <p className="text-[#ccc] text-center">
                  {searchTerm
                    ? "No hidden performers match your search."
                    : "No hidden performers found."}
                </p>
              ) : (
                <ul className="list-none p-0">
                  {sortedHiddenPerformers.map((performer) => (
                    <li
                      key={performer._id}
                      className="flex flex-col md:flex-row justify-between items-center py-2 border-b border-[#444]"
                    >
                      <span className="text-white flex-1 text-center md:text-left">
                        {performer.name}
                      </span>
                      <div className="flex flex-row gap-2 mt-2 md:mt-0">
                        <button
                          onClick={() => editPerformer(performer)}
                          className="px-3 py-1 bg-[#4CAF50] hover:bg-[#45a049] text-white text-sm rounded transition-colors cursor-pointer"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => unhidePerformer(performer._id)}
                          className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded transition-colors cursor-pointer"
                        >
                          Unhide
                        </button>
                        {performer.published ? (
                          <button
                            onClick={() => unpublishPerformer(performer._id)}
                            className="px-3 py-1 bg-[#d4a017] hover:bg-[#b38b00] text-white text-sm rounded transition-colors cursor-pointer"
                          >
                            Unpublish
                          </button>
                        ) : (
                          <button
                            onClick={() => publishPerformer(performer._id)}
                            className="px-3 py-1 bg-[#d4a017] hover:bg-[#b38b00] text-white text-sm rounded transition-colors cursor-pointer"
                          >
                            Publish
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )
      }
    </div>
  );
}

export default PerformerTab;
